<template>
  <div class="date-range-picker">
    <!-- Quick Presets -->
    <div class="presets-section mb-4">
      <v-btn-group
        v-model="selectedPreset"
        density="compact"
        variant="outlined"
        divided
      >
        <v-btn
          v-for="preset in presets"
          :key="preset.value"
          :value="preset.value"
          size="small"
          @click="selectPreset(preset)"
        >
          {{ preset.label }}
        </v-btn>
      </v-btn-group>
    </div>

    <!-- Custom Date Selection -->
    <v-row dense>
      <v-col cols="6">
        <v-text-field
          v-model="startDateInput"
          label="Fecha inicio"
          type="date"
          variant="outlined"
          density="compact"
          prepend-inner-icon="mdi-calendar"
          @update:model-value="updateStartDate"
        />
      </v-col>
      
      <v-col cols="6">
        <v-text-field
          v-model="endDateInput"
          label="Fecha fin"
          type="date"
          variant="outlined"
          density="compact"
          prepend-inner-icon="mdi-calendar"
          @update:model-value="updateEndDate"
        />
      </v-col>
    </v-row>

    <!-- Range Info -->
    <div class="range-info mt-3">
      <v-chip
        color="primary"
        variant="outlined"
        size="small"
        prepend-icon="mdi-clock-outline"
      >
        {{ rangeDuration }} días
      </v-chip>
      
      <v-chip
        color="info"
        variant="outlined"
        size="small"
        prepend-icon="mdi-calendar-range"
        class="ml-2"
      >
        {{ formatDateRange }}
      </v-chip>
    </div>

    <!-- Advanced Options -->
    <v-expand-transition>
      <div v-show="showAdvanced" class="advanced-options mt-4">
        <v-divider class="mb-3"></v-divider>
        
        <v-row dense>
          <v-col cols="12">
            <v-select
              v-model="timezone"
              :items="timezones"
              label="Zona horaria"
              variant="outlined"
              density="compact"
              prepend-inner-icon="mdi-earth"
            />
          </v-col>
        </v-row>
        
        <v-row dense>
          <v-col cols="6">
            <v-select
              v-model="startTime"
              :items="timeOptions"
              label="Hora inicio"
              variant="outlined"
              density="compact"
              prepend-inner-icon="mdi-clock"
            />
          </v-col>
          
          <v-col cols="6">
            <v-select
              v-model="endTime"
              :items="timeOptions"
              label="Hora fin"
              variant="outlined"
              density="compact"
              prepend-inner-icon="mdi-clock"
            />
          </v-col>
        </v-row>
        
        <v-switch
          v-model="includeTime"
          label="Incluir componente de tiempo"
          color="primary"
          density="compact"
          class="mt-2"
        />
      </div>
    </v-expand-transition>

    <!-- Toggle Advanced -->
    <div class="text-center mt-3">
      <v-btn
        variant="text"
        size="small"
        @click="showAdvanced = !showAdvanced"
        :prepend-icon="showAdvanced ? 'mdi-chevron-up' : 'mdi-chevron-down'"
      >
        {{ showAdvanced ? 'Menos opciones' : 'Más opciones' }}
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { format, differenceInDays, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import type { DateRange } from '@/types/analytics'

// 📝 Props & Emits
interface Props {
  modelValue: DateRange
  presets?: Array<{
    label: string
    value: string
    range: () => DateRange
  }>
}

const props = withDefaults(defineProps<Props>(), {
  presets: () => []
})

const emit = defineEmits<{
  'update:modelValue': [range: DateRange]
  update: [range: DateRange]
}>()

// 🔄 Reactive State
const selectedPreset = ref<string | null>(null)
const showAdvanced = ref(false)
const includeTime = ref(false)
const timezone = ref('America/Santo_Domingo')
const startTime = ref('00:00')
const endTime = ref('23:59')

// 📅 Date Inputs (as strings for v-text-field)
const startDateInput = ref('')
const endDateInput = ref('')

// 🌍 Timezones
const timezones = [
  { title: 'República Dominicana (UTC-4)', value: 'America/Santo_Domingo' },
  { title: 'Nueva York (UTC-5)', value: 'America/New_York' },
  { title: 'Los Ángeles (UTC-8)', value: 'America/Los_Angeles' },
  { title: 'Londres (UTC+0)', value: 'Europe/London' },
  { title: 'Madrid (UTC+1)', value: 'Europe/Madrid' },
  { title: 'Tokio (UTC+9)', value: 'Asia/Tokyo' }
]

// 🕐 Time Options
const timeOptions = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0')
  return { title: `${hour}:00`, value: `${hour}:00` }
})

// 📊 Computed Properties
const rangeDuration = computed(() => {
  try {
    return differenceInDays(props.modelValue.end, props.modelValue.start) + 1
  } catch {
    return 0
  }
})

const formatDateRange = computed(() => {
  try {
    const start = format(props.modelValue.start, 'dd/MM/yyyy', { locale: es })
    const end = format(props.modelValue.end, 'dd/MM/yyyy', { locale: es })
    return `${start} - ${end}`
  } catch {
    return 'Fechas inválidas'
  }
})

// 🔄 Methods
const selectPreset = (preset: any) => {
  const range = preset.range()
  updateRange(range)
  selectedPreset.value = preset.value
}

const updateStartDate = (dateString: string) => {
  if (dateString) {
    try {
      const newStart = parseISO(dateString)
      updateRange({ start: newStart, end: props.modelValue.end })
      selectedPreset.value = null // Clear preset selection
    } catch (error) {
      console.error('Invalid start date:', error)
    }
  }
}

const updateEndDate = (dateString: string) => {
  if (dateString) {
    try {
      const newEnd = parseISO(dateString)
      updateRange({ start: props.modelValue.start, end: newEnd })
      selectedPreset.value = null // Clear preset selection
    } catch (error) {
      console.error('Invalid end date:', error)
    }
  }
}

const updateRange = (range: DateRange) => {
  // Apply time component if enabled
  if (includeTime.value) {
    const [startHour, startMinute] = startTime.value.split(':').map(Number)
    const [endHour, endMinute] = endTime.value.split(':').map(Number)
    
    range.start.setHours(startHour, startMinute, 0, 0)
    range.end.setHours(endHour, endMinute, 59, 999)
  } else {
    // Set to start and end of day
    range.start.setHours(0, 0, 0, 0)
    range.end.setHours(23, 59, 59, 999)
  }
  
  emit('update:modelValue', range)
  emit('update', range)
}

const formatDateForInput = (date: Date): string => {
  try {
    return format(date, 'yyyy-MM-dd')
  } catch {
    return ''
  }
}

const syncInputsWithModelValue = () => {
  startDateInput.value = formatDateForInput(props.modelValue.start)
  endDateInput.value = formatDateForInput(props.modelValue.end)
}

// 👀 Watchers
watch(() => props.modelValue, syncInputsWithModelValue, { immediate: true })

watch([includeTime, startTime, endTime], () => {
  if (includeTime.value) {
    updateRange({ ...props.modelValue })
  }
})

// 🎯 Initialize
syncInputsWithModelValue()
</script>

<style scoped>
.date-range-picker {
  padding: 0;
}

.presets-section {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.range-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.advanced-options {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

@media (max-width: 600px) {
  .presets-section .v-btn-group {
    width: 100%;
    flex-direction: column;
  }
  
  .range-info {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
